import { useId } from 'react';
import { classNames } from '~/utils/classNames';
import { FieldInput, type ReferenceLoader } from './FieldInput';
import type { FieldDefinition } from './sdk';

export interface FieldsFormProps {
  fields: readonly FieldDefinition[];
  values: Record<string, unknown>;
  onChange: (next: Record<string, unknown>) => void;
  /** Errors by field key (from validateFields or studio's `errors`). */
  errors?: Record<string, string>;
  disabled?: boolean;
  references?: ReferenceLoader;
  className?: string;
}

/**
 * A form generated from FieldDefinitions: label, input, help text and error
 * per field. Controlled; validation is the caller's (draftErrors in
 * field-model.ts runs the same validateFields studio does).
 */
export function FieldsForm({ fields, values, onChange, errors, disabled, references, className }: FieldsFormProps) {
  const base = useId();

  return (
    <div className={classNames('space-y-4', className)}>
      {fields.map((field) => {
        const id = `${base}-${field.key}`;
        const error = errors?.[field.key];

        return (
          <div key={field.key} className="space-y-1.5">
            <label htmlFor={id} className="flex items-baseline gap-1 text-[11px] text-secondary">
              {field.label}
              {field.required && (
                <span className="text-muted" aria-label="required">
                  *
                </span>
              )}
            </label>
            <FieldInput
              field={field}
              id={id}
              value={values[field.key]}
              invalid={Boolean(error)}
              disabled={disabled}
              references={references}
              onChange={(next) => onChange({ ...values, [field.key]: next })}
            />
            {error ? (
              <span className="block text-[11px] leading-relaxed text-danger">{error}</span>
            ) : field.help ? (
              <span className="block text-[11px] leading-relaxed text-muted">{field.help}</span>
            ) : null}
          </div>
        );
      })}
    </div>
  );
}
